import ListFilters from '@/common/components/ListFilters'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { useEquipmentManufacturers } from '@/common/hooks/queries/useEquipmentManufacturers'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { EquipmentManufacturersData } from '@/common/types/equipmentManufacturers'
import { pendingColumns } from '@/features/modelIntegrationCodes/constants/columns.constant'
import { useModelIntegrationCodes } from '@/features/modelIntegrationCodes/hooks/useModelIntegrationCodes'
import { ModelIntegrationCodesTransformer } from '@/features/modelIntegrationCodes/services/modelIntegrationCodesTransformer'
import {
  ModelIntegrationCode,
  ModelIntegrationCodeTableItem,
} from '@/features/modelIntegrationCodes/types/modelIntegrationCode.type'
import { SearchIcon } from 'lucide-react'

const PendingList = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [manufacturerSearch, setManufacturerSearch] = useDebounce('')

  const { data: manufacturerOptions = [] } = useEquipmentManufacturers({
    filters: { search: manufacturerSearch },
    mapper: Mapper.mapToOptions<EquipmentManufacturersData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const {
    data: items = [],
    meta,
    isPending,
    isFetching,
  } = useModelIntegrationCodes<ModelIntegrationCodeTableItem[]>({
    filters: {
      search,
      manufacturer_id: filterParams.manufacturer?.value,
      page: Number(filterParams.page) || 1,
    },
    mapper: Mapper.mapWithTransformer<ModelIntegrationCode[], ModelIntegrationCodeTableItem[]>({
      transformer: ModelIntegrationCodesTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-4">
      <TextField
        LeftIcon={<SearchIcon />}
        placeholder="Busque por nome do modelo"
        className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
        onChange={(event) => {
          const value = (event.target as HTMLInputElement).value
          setSearch(value)
          setFilterParams((prev) => ({ ...prev, search: value, page: 1 }))
        }}
        value={filterParams.search || ''}
        dataTestid="model_integration_codes_search"
      />

      <ListFilters
        filters={[
          {
            placeholder: 'Fabricante',
            options: [{ label: 'Todos', value: '' }, ...manufacturerOptions],
            value: filterParams.manufacturer || null,
            onChange: (selected) => setFilterParams((prev) => ({ ...prev, manufacturer: selected })),
            onInputChange: setManufacturerSearch,
            dataTestid: 'manufacturer_filter',
            operationKlassToCheckPermission: 'Admin::EquipmentModels::PendingIntegrationFilter',
          },
        ]}
      />

      <Table
        columns={pendingColumns}
        rows={items}
        isLoadingData={isPending || isFetching}
        page={Number(filterParams.page) || 1}
        totalPages={meta?.total_pages || 1}
        totalCount={meta?.total || 0}
        hasPagination
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
        rowEmptyValue="--"
      />
    </div>
  )
}

export default PendingList
